import {
  apiBootstrap,
  apiInsert,
  apiUpdate,
  apiDelete,
} from './api-client'
import { normalizeCampStatus } from '@/lib/constants'
import type { AppState } from '@/store/reducer'
import type {
  Camp,
  Partner,
  ItineraryDay,
  Activity,
  Quote,
  QuoteItem,
  PaymentMilestone,
  Participant,
  Task,
  Note,
  CampPartner,
  ExpenseActual,
  ExpenseEstimate,
} from '@/types/database'

// ── Row helpers ──────────────────────────────────────────────────────────────
// All writes go to the Rust API via the same-origin BFF proxy.

function dbInsert<T>(table: string, row: Partial<T>): Promise<T> {
  return apiInsert<T>(table, row)
}

function dbUpdate<T>(table: string, id: string, updates: Partial<T>): Promise<T> {
  return apiUpdate<T>(table, id, updates)
}

function dbDelete(table: string, id: string): Promise<void> {
  return apiDelete(table, id)
}

// ==================== Initial Load ====================

/**
 * Bulk load every entity array for an organization (or all rows the session can
 * read when no organization context is available) from the Rust API /bootstrap
 * endpoint via the BFF proxy.
 */
function loadRaw(
  organizationId: string | null,
): Promise<Record<string, unknown[] | null | undefined>> {
  return apiBootstrap(organizationId)
}

export async function fetchAllData(organizationId: string | null): Promise<AppState> {
  const raw = await loadRaw(organizationId)

  // Cast results to typed arrays (keys match in both backends)
  const typedCamps = ((raw.camps ?? []) as Camp[]).map((c) => ({
    ...c,
    status: normalizeCampStatus(c.status),
  }))
  const typedPartners = (raw.partners ?? []) as Partner[]
  const typedDays = (raw.itineraryDays ?? []) as ItineraryDay[]
  const typedActivities = (raw.activities ?? []) as Activity[]
  const typedQuotes = (raw.quotes ?? []) as Quote[]
  const typedQuoteItems = (raw.quoteItems ?? []) as QuoteItem[]
  const typedMilestones = (raw.paymentMilestones ?? []) as PaymentMilestone[]
  const typedParticipants = (raw.participants ?? []) as Participant[]
  const typedTasks = (raw.tasks ?? []) as Task[]
  const typedNotes = (raw.notes ?? []) as Note[]
  const typedTripPartners = (raw.campPartners ?? []) as CampPartner[]
  const typedExpenseActuals = (raw.expenseActuals ?? []) as ExpenseActual[]
  const typedExpenseEstimates = (raw.expenseEstimates ?? []) as ExpenseEstimate[]

  // Build campData keyed by trip ID
  const campData: AppState['campData'] = {}

  // Build day-to-trip mapping for activities
  const dayToTrip = new Map<string, string>()
  for (const day of typedDays) {
    dayToTrip.set(day.id, day.camp_id)
  }

  // Build quote-to-trip mapping for quote items
  const quoteToTrip = new Map<string, string>()
  for (const quote of typedQuotes) {
    quoteToTrip.set(quote.id, quote.camp_id)
  }

  // Initialize empty data for each trip
  for (const camp of typedCamps) {
    campData[camp.id] = {
      itineraryDays: [],
      activities: [],
      quotes: [],
      quoteItems: [],
      paymentMilestones: [],
      participants: [],
      tasks: [],
      notes: [],
      campPartners: [],
      expenseActuals: [],
      expenseEstimates: [],
    }
  }

  for (const day of typedDays) {
    campData[day.camp_id]?.itineraryDays.push(day)
  }

  // activities に camp_id は無く、日を経由してしか案件に結びつけられない。
  //
  // なお「日が消えているのにアクティビティだけ残っている」状態は、ここでは
  // 検知できない。サーバの bootstrap が「存在する日に紐づくもの」だけを返すため、
  // そもそもクライアントに届かないため。検知するならサーバ側でやる必要がある。
  for (const activity of typedActivities) {
    const campId = dayToTrip.get(activity.itinerary_day_id)
    if (campId && campData[campId]) {
      campData[campId].activities.push(activity)
    }
  }

  for (const quote of typedQuotes) {
    campData[quote.camp_id]?.quotes.push(quote)
  }

  for (const item of typedQuoteItems) {
    const campId = quoteToTrip.get(item.quote_id)
    if (campId && campData[campId]) {
      campData[campId].quoteItems.push(item)
    }
  }

  for (const milestone of typedMilestones) {
    campData[milestone.camp_id]?.paymentMilestones.push(milestone)
  }

  for (const participant of typedParticipants) {
    campData[participant.camp_id]?.participants.push(participant)
  }

  for (const task of typedTasks) {
    campData[task.camp_id]?.tasks.push(task)
  }

  for (const note of typedNotes) {
    campData[note.camp_id]?.notes.push(note)
  }

  for (const tp of typedTripPartners) {
    campData[tp.camp_id]?.campPartners.push(tp)
  }

  for (const ea of typedExpenseActuals) {
    campData[ea.camp_id]?.expenseActuals.push(ea)
  }

  for (const ee of typedExpenseEstimates) {
    campData[ee.camp_id]?.expenseEstimates.push(ee)
  }

  return {
    camps: typedCamps,
    partners: typedPartners,
    campData,
  }
}

// ==================== Trip CRUD ====================

export const createTrip = (camp: Partial<Camp>) => dbInsert<Camp>('camps', camp)
export const updateTrip = (id: string, updates: Partial<Camp>) => dbUpdate<Camp>('camps', id, updates)
export const deleteTrip = (id: string) => dbDelete('camps', id)

// ==================== Itinerary ====================

export const createItineraryDay = (day: Partial<ItineraryDay>) =>
  dbInsert<ItineraryDay>('itinerary_days', day)
export const updateItineraryDay = (id: string, updates: Partial<ItineraryDay>) =>
  dbUpdate<ItineraryDay>('itinerary_days', id, updates)

export async function deleteItineraryDay(id: string): Promise<void> {
  // Relies on the FK ON DELETE CASCADE for activities on the Rust side.
  await dbDelete('itinerary_days', id)
}

export const createActivity = (activity: Partial<Activity>) =>
  dbInsert<Activity>('activities', activity)
export const updateActivity = (id: string, updates: Partial<Activity>) =>
  dbUpdate<Activity>('activities', id, updates)
export const deleteActivity = (id: string) => dbDelete('activities', id)

export async function updateActivitiesSortOrder(
  updates: Array<{ id: string; sort_order: number }>,
): Promise<void> {
  await Promise.all(updates.map(({ id, sort_order }) => apiUpdate('activities', id, { sort_order })))
}

// ==================== Quotes ====================

export const createQuote = (quote: Partial<Quote>) => dbInsert<Quote>('quotes', quote)
export const updateQuote = (id: string, updates: Partial<Quote>) =>
  dbUpdate<Quote>('quotes', id, updates)

export function createQuoteItem(item: Partial<QuoteItem>): Promise<QuoteItem> {
  // `source` is dropped from writes (not a column); cogs-auto rows stay
  // identifiable by their description marker.
  const { source: _s, ...payload } = item
  return dbInsert<QuoteItem>('quote_items', payload)
}

export function updateQuoteItem(id: string, updates: Partial<QuoteItem>): Promise<QuoteItem> {
  const { source: _s, ...payload } = updates
  return dbUpdate<QuoteItem>('quote_items', id, payload)
}

export const deleteQuoteItem = (id: string) => dbDelete('quote_items', id)

// ==================== Payments ====================

export const createPaymentMilestone = (m: Partial<PaymentMilestone>) =>
  dbInsert<PaymentMilestone>('payment_milestones', m)
export const updatePaymentMilestone = (id: string, updates: Partial<PaymentMilestone>) =>
  dbUpdate<PaymentMilestone>('payment_milestones', id, updates)
export const deletePaymentMilestone = (id: string) => dbDelete('payment_milestones', id)

// ==================== Participants ====================

export const createParticipant = (p: Partial<Participant>) =>
  dbInsert<Participant>('participants', p)
export const updateParticipant = (id: string, updates: Partial<Participant>) =>
  dbUpdate<Participant>('participants', id, updates)
export const deleteParticipant = (id: string) => dbDelete('participants', id)

// ==================== Tasks ====================

export function createTask(t: Partial<Task>): Promise<Task> {
  // updated_at is DB-owned (default + trigger). Never send it from the client.
  const { updated_at: _ua, ...payload } = t
  return dbInsert<Task>('tasks', payload)
}

export function updateTask(id: string, updates: Partial<Task>): Promise<Task> {
  const { updated_at: _ua, ...payload } = updates
  return dbUpdate<Task>('tasks', id, payload)
}

export const deleteTask = (id: string) => dbDelete('tasks', id)

// ==================== Notes ====================

export function createNote(n: Partial<Note>): Promise<Note> {
  const { updated_at: _ua, ...payload } = n
  return dbInsert<Note>('notes', payload)
}

export function updateNote(id: string, updates: Partial<Note>): Promise<Note> {
  const { updated_at: _ua, ...payload } = updates
  return dbUpdate<Note>('notes', id, payload)
}

export const deleteNote = (id: string) => dbDelete('notes', id)

// ==================== Partners ====================

export const createPartner = (p: Partial<Partner>) => dbInsert<Partner>('partners', p)
export const updatePartner = (id: string, updates: Partial<Partner>) =>
  dbUpdate<Partner>('partners', id, updates)
export const deletePartner = (id: string) => dbDelete('partners', id)

// ==================== Expense Actuals ====================

export const createExpenseActual = (ea: Partial<ExpenseActual>) =>
  dbInsert<ExpenseActual>('expense_actuals', ea)
export const updateExpenseActual = (id: string, updates: Partial<ExpenseActual>) =>
  dbUpdate<ExpenseActual>('expense_actuals', id, updates)
export const deleteExpenseActual = (id: string) => dbDelete('expense_actuals', id)

// ==================== Expense Estimates ====================

export const createExpenseEstimate = (ee: Partial<ExpenseEstimate>) =>
  dbInsert<ExpenseEstimate>('expense_estimates', ee)
export const updateExpenseEstimate = (id: string, updates: Partial<ExpenseEstimate>) =>
  dbUpdate<ExpenseEstimate>('expense_estimates', id, updates)
export const deleteExpenseEstimate = (id: string) => dbDelete('expense_estimates', id)

// ==================== Trip-Partner ====================

export const assignPartnerToTrip = (tp: Partial<CampPartner>) =>
  dbInsert<CampPartner>('camp_partners', tp)
export const removePartnerFromTrip = (id: string) => dbDelete('camp_partners', id)
